import React, { useMemo, useState } from 'react';
import { ArrowLeftIcon, CameraIcon, CheckCircle2Icon, ClockIcon, FileIcon, ImageIcon, PackageIcon, PlayIcon, PlusIcon, ReceiptIcon, Trash2Icon, WrenchIcon } from 'lucide-react';
import { Employee, JobStatus, Material, Product, ProJob, TimeEntry, WorkType } from '../../types/pro';
import { JOB_STATUS, formatDate, formatTime, hoursOnJob, kr, newLineId, toISODate } from '../../utils/pro';
import { StatusChip } from '../../components/pro/StatusChip';
import { EmptyState } from '../../components/pro/EmptyState';
import { TimeEntryList } from '../../components/pro/TimeEntryList';
import { JobLineAdder } from '../../components/pro/JobLineAdder';
interface ProJobDetailProps {
  job: ProJob;
  employees: Employee[];
  materials: Material[];
  /** Services — servicebil, kørsel, dokumentation. */
  products: Product[];
  timeEntries: TimeEntry[];
  companyHourlyRate: number;
  onBack: () => void;
  onUpdate: (changes: Partial<ProJob>) => void;
  onAddTime: (entry: Omit<TimeEntry, 'id'>) => void;
  onUpdateTime: (id: string, changes: Partial<TimeEntry>) => void;
  onRemoveTime: (id: string) => void;
  onCreateInvoice: (job: ProJob) => void;
}
type Tab = 'overblik' | 'timer' | 'materialer' | 'billeder' | 'dokumenter' | 'noter';
const TABS: {
  id: Tab;
  label: string;
}[] = [{
  id: 'overblik',
  label: 'Overblik'
}, {
  id: 'timer',
  label: 'Timer'
}, {
  id: 'materialer',
  label: 'Materialer og ydelser'
}, {
  id: 'billeder',
  label: 'Billeder'
}, {
  id: 'dokumenter',
  label: 'Dokumenter'
}, {
  id: 'noter',
  label: 'Noter'
}];
const WORK_TYPES: WorkType[] = ['Montage', 'Fejlfinding', 'Service', 'Kørsel', 'Andet'];
export function ProJobDetail({
  job,
  employees,
  materials,
  products,
  timeEntries,
  companyHourlyRate,
  onBack,
  onUpdate,
  onAddTime,
  onUpdateTime,
  onRemoveTime,
  onCreateInvoice
}: ProJobDetailProps) {
  const [tab, setTab] = useState<Tab>('overblik');
  const [timeForm, setTimeForm] = useState({
    employeeId: employees[0]?.id ?? '',
    hours: '',
    workType: 'Montage' as WorkType,
    note: ''
  });
  const own = useMemo(() => timeEntries.filter(entry => entry.jobId === job.id), [timeEntries, job.id]);
  const registered = hoursOnJob(timeEntries, job.id ?? '');
  const overrun = job.estimatedHours > 0 && registered > job.estimatedHours;
  const materialTotal = job.materials.reduce((sum, item) => sum + item.quantity * item.salesPrice, 0);
  const usesOwnRate = typeof job.hourlyRate === 'number';
  const rate = usesOwnRate ? job.hourlyRate as number : companyHourlyRate;
  // Older jobs have no billing type saved; they are all regningsarbejde.
  const billing = job.billingType ?? 'regning';
  const labourTotal = registered * rate;
  const setStatus = (status: JobStatus) => onUpdate({
    status
  });
  const addTime = () => {
    const hours = parseFloat(timeForm.hours.replace(',', '.'));
    if (!hours || !timeForm.employeeId || !job.id) return;
    onAddTime({
      employeeId: timeForm.employeeId,
      jobId: job.id,
      workType: timeForm.workType,
      date: toISODate(new Date()),
      hours,
      note: timeForm.note.trim(),
      billable: timeForm.workType !== 'Kørsel'
    });
    setTimeForm({
      ...timeForm,
      hours: '',
      note: ''
    });
  };
  const card = 'rounded-xl border border-stone-200 bg-white';
  const field = 'w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500';
  const actions = <>
      {job.status !== 'i-gang' && job.status !== 'faerdig' && <button onClick={() => setStatus('i-gang')} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-stone-900 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-stone-800" data-id="element-1605" data-mp="34l49zrvty19w">
          <PlayIcon className="h-4 w-4" data-id="element-1606" data-mp="274nlfr6x06c8" />
          Start arbejde
        </button>}
      <button onClick={() => setTab('timer')} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-stone-200 bg-white px-4 py-3 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50" data-id="element-1607" data-mp="1ad0mkr98kekb">
        <ClockIcon className="h-4 w-4" data-id="element-1608" data-mp="1jc5lvzsmgbxj" />
        Registrer tid
      </button>
      <button onClick={() => setTab('materialer')} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-stone-200 bg-white px-4 py-3 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50" data-id="element-1609" data-mp="1o4yoah0mb86g">
        <PackageIcon className="h-4 w-4" data-id="element-1610" data-mp="230wihtqze2g1" />
        Tilføj varer
      </button>
      {job.status !== 'faerdig' && job.status !== 'faktureret' ? <button onClick={() => setStatus('faerdig')} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-700" data-id="element-1611" data-mp="2728au718p1rx">
          <CheckCircle2Icon className="h-4 w-4" data-id="element-1612" data-mp="560p7onh3wcw" />
          Afslut opgave
        </button> : job.status === 'faerdig' && <button onClick={() => onCreateInvoice(job)} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1613" data-mp="dg0v2nc6bgr">
            <ReceiptIcon className="h-4 w-4" data-id="element-1614" data-mp="o4li9m8xdbt6" />
            Opret faktura
          </button>}
    </>;
  return <div className="pb-24 lg:pb-0" data-id="element-1615" data-mp="j5s2toa2vaqd">
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-stone-500 transition-colors hover:text-stone-900" data-id="element-1616" data-mp="f3n9yrrgei4s">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-1617" data-mp="a6ohsercol98" />
        Alle opgaver
      </button>

      <header className="mb-5 flex flex-wrap items-start justify-between gap-4" data-id="element-1618" data-mp="2zvh2ufjrsjbh">
        <div className="min-w-0" data-id="element-1619" data-mp="2utrninpiljhl">
          <div className="flex flex-wrap items-center gap-2.5" data-id="element-1620" data-mp="2uww12bovdrkx">
            <p className="font-mono text-xs font-semibold text-brand-700" data-id="element-1621" data-mp="2wd03hhrymtjr">
              {job.jobNo}
            </p>
            <StatusChip meta={JOB_STATUS[job.status]} data-id="element-1622" data-mp="31ajgkecx90a4" />
          </div>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-stone-900 sm:text-3xl" data-id="element-1623" data-mp="2nc1usm6ktxuu">
            {job.title}
          </h1>
          <p className="mt-1 text-sm text-stone-500" data-id="element-1624" data-mp="2rb2b9y1l0oeo">
            {job.customerName} · {job.address}, {job.city}
          </p>
        </div>
        <label className="flex-shrink-0" data-id="element-1625" data-mp="2ibxdwv1rb143">
          <span className="sr-only" data-id="element-1626" data-mp="2icz0nws4xc1n">Skift status</span>
          <select value={job.status} onChange={e => setStatus(e.target.value as JobStatus)} className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-medium outline-none focus:ring-2 focus:ring-brand-500" data-id="element-1627" data-mp="23nkqwnkncslq">
            {(Object.keys(JOB_STATUS) as JobStatus[]).map(status => <option key={status} value={status} data-id="element-1628" data-mp="1zl6986spryw1">
                {JOB_STATUS[status].label}
              </option>)}
          </select>
        </label>
      </header>

      {/* Estimated vs registered, side by side while the job is still running */}
      <section className={`${card} mb-5 grid grid-cols-2 gap-px overflow-hidden bg-stone-200 sm:grid-cols-4`} data-id="element-1629" data-mp="1p13w3qcd6efg">
        {[{
        label: 'Estimeret',
        value: `${job.estimatedHours} t`
      }, {
        label: 'Registreret',
        value: `${registered.toLocaleString('da-DK')} t`,
        alert: overrun
      }, {
        label: 'Materialer m.m.',
        value: kr(materialTotal)
      }, billing === 'fast-pris' ? {
        label: 'Fast pris',
        value: kr(job.fixedPrice ?? 0),
        hint: `Forbrug: ${kr(labourTotal + materialTotal)}`
      } : {
        label: 'Arbejdsløn',
        value: kr(labourTotal),
        hint: `${rate.toLocaleString('da-DK')} kr/t${usesOwnRate ? ' · egen pris' : ''}`
      }].map(stat => <div key={stat.label} className="bg-white p-4" data-id="element-1630" data-mp="3p0xgq02djlm4">
            <p className={`text-[11px] font-semibold uppercase tracking-wider ${stat.alert ? 'text-amber-600' : 'text-stone-400'}`} data-id="element-1631" data-mp="3p3jnhxhfxx3a">
              {stat.label}
            </p>
            <p className={`mt-1 text-lg font-bold tabular-nums ${stat.alert ? 'text-amber-600' : 'text-stone-900'}`} data-id="element-1632" data-mp="31cxjqiegyyz3">
              {stat.value}
            </p>
            {stat.hint && <p className={`mt-0.5 text-xs ${usesOwnRate ? 'font-medium text-brand-700' : 'text-stone-400'}`} data-id="element-1633" data-mp="3aaq0lqzuaeol">
                {stat.hint}
              </p>}
          </div>)}
      </section>

      <div role="tablist" aria-label="Opgavesektioner" className="mb-5 flex gap-1 overflow-x-auto rounded-xl border border-stone-200 bg-white p-1" data-id="element-1634" data-mp="2mlnkwoormvwq">
        {TABS.map(item => <button key={item.id} role="tab" aria-selected={tab === item.id} onClick={() => setTab(item.id)} className={`whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${tab === item.id ? 'bg-brand-50 text-brand-700' : 'text-stone-500 hover:text-stone-900'}`} data-id="element-1635" data-mp="2a5fkilrw76dp">
            {item.label}
          </button>)}
      </div>

      {tab === 'overblik' && <div className="space-y-5" data-id="element-1636" data-mp="1hfyshp86pri0">
          <section className={`${card} p-5`} data-id="element-1637" data-mp="1hgqfwcrmoix7">
            <h2 className="mb-3 text-base font-semibold text-stone-900" data-id="element-1638" data-mp="1hdvuoj0siu2q">
              Beskrivelse
            </h2>
            <textarea value={job.description} onChange={e => onUpdate({
          description: e.target.value
        })} rows={4} className={field} data-id="element-1639" data-mp="18fadar721l3j" />
          </section>

          <section className={`${card} p-5`} data-id="element-1640" data-mp="2q9hgyayjn7zb">
            <h2 className="mb-4 text-base font-semibold text-stone-900" data-id="element-1641" data-mp="2q8o6aywgrybw">
              Planlægning
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-1642" data-mp="34zop9xuu7ama">
              <label className="block" data-id="element-1643" data-mp="2zz9dvbu48d6y">
                <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1644" data-mp="2c96pb5petpd3">
                  Start
                </span>
                <input type="datetime-local" value={job.start ? job.start.slice(0, 16) : ''} onChange={e => onUpdate({
              start: e.target.value ? new Date(e.target.value).toISOString() : undefined,
              status: job.status === 'ikke-planlagt' && e.target.value ? 'planlagt' : job.status
            })} className={field} data-id="element-1645" data-mp="27avf2tv7dcuc" />
              </label>
              <label className="block" data-id="element-1646" data-mp="91xxla7uzyx3">
                <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1647" data-mp="3r6j2xobyfeku">
                  Varighed (timer)
                </span>
                <input type="number" min={1} value={job.durationHours} onChange={e => onUpdate({
              durationHours: parseFloat(e.target.value) || 1
            })} className={field} data-id="element-1648" data-mp="3v8cy5h3drqdm" />
              </label>
              <label className="block" data-id="element-1649" data-mp="dxwnx3cqzr05">
                <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1650" data-mp="i09jiyxs68j7">
                  Estimerede timer
                </span>
                <input type="number" min={0} value={job.estimatedHours} onChange={e => onUpdate({
              estimatedHours: parseFloat(e.target.value) || 0
            })} className={field} data-id="element-1651" data-mp="15osr1c90aha8" />
              </label>
            </div>

            {/* How the job is billed. Regningsarbejde invoices what was
                actually used; a fixed price invoices the agreed sum. */}
            <div className="mt-4 border-t border-stone-100 pt-4" data-id="element-1652" data-mp="16fxhdadbzqme">
              <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1653" data-mp="11fjj2k1j7v1k">
                Afregning
              </span>
              <div className="flex flex-wrap gap-2" data-id="element-1654" data-mp="wlx4unfpzj3n">
                {[{
              id: 'regning' as const,
              label: 'Regningsarbejde'
            }, {
              id: 'fast-pris' as const,
              label: 'Fast pris'
            }].map(option => <button key={option.id} onClick={() => onUpdate({
              billingType: option.id
            })} aria-pressed={billing === option.id} className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${billing === option.id ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1655" data-mp="ioidhd5wp191">
                    {option.label}
                  </button>)}
              </div>

              {billing === 'fast-pris' && <label className="mt-3 block max-w-xs" data-id="element-1656" data-mp="3gy0sfi3nwzh5">
                  <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1657" data-mp="3m09nck03iblw">
                    Aftalt pris ekskl. moms (kr)
                  </span>
                  <input type="number" min={0} value={job.fixedPrice ?? 0} onChange={e => onUpdate({
              fixedPrice: Math.max(0, parseFloat(e.target.value) || 0)
            })} className={field} data-id="element-1658" data-mp="3vp1a8dqau5py" />
                  <span className="mt-1 block text-xs text-stone-400" data-id="element-1659" data-mp="18q5n6xqpvw1y">
                    Fakturaen oprettes med dette beløb, uanset forbrugt tid.
                  </span>
                </label>}
            </div>

            {billing === 'regning' && <div className="mt-4 border-t border-stone-100 pt-4" data-id="element-1660" data-mp="1dq0h7a5sh1r8">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-1661" data-mp="1ri8agrxv6al6">
                <label className="block" data-id="element-1662" data-mp="1nhek7rwy8jp4">
                  <span className="mb-1.5 block text-sm font-medium text-stone-700" data-id="element-1663" data-mp="1ngd0kz1vaovs">
                    Timepris (kr)
                  </span>
                  <input type="number" min={0} step={5} value={rate} onChange={e => onUpdate({
                hourlyRate: Math.max(0, parseFloat(e.target.value) || 0)
              })} className={field} data-id="element-1664" data-mp="21etgnglmdrql" />
                </label>
                <div className="sm:col-span-2 sm:pt-7" data-id="element-1665" data-mp="2ir1e8qlpneyq">
                  <p className="text-sm text-stone-500" data-id="element-1666" data-mp="2iskuih7d9q0i">
                    {usesOwnRate ? <>
                        Denne opgave bruger sin egen pris. Firmaets standard er{' '}
                        {companyHourlyRate.toLocaleString('da-DK')} kr/t.{' '}
                        <button onClick={() => onUpdate({
                    hourlyRate: null
                  })} className="font-medium text-brand-700 underline underline-offset-2 transition-colors hover:text-brand-800" data-id="element-1667" data-mp="1uzm78kizo9h5">
                          Brug standardprisen
                        </button>
                      </> : <>
                        Følger firmaets standardpris. Ændrer du tallet, gælder
                        det kun denne opgave — og slår igennem på fakturaen.
                      </>}
                  </p>
                </div>
              </div>
            </div>}

            <fieldset className="mt-4" data-id="element-1668" data-mp="3e1oh4t43tfac">
              <legend className="mb-2 text-sm font-medium text-stone-700" data-id="element-1669" data-mp="38zz63dgvt1m9">
                Tildelte medarbejdere
              </legend>
              <div className="flex flex-wrap gap-2" data-id="element-1670" data-mp="350hyaddzfsdx">
                {employees.map(employee => {
              const isOn = job.employeeIds.includes(employee.id ?? '');
              return <button key={employee.id} onClick={() => onUpdate({
                employeeIds: isOn ? job.employeeIds.filter(id => id !== employee.id) : [...job.employeeIds, employee.id ?? '']
              })} aria-pressed={isOn} className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${isOn ? 'border-brand-300 bg-brand-50 text-brand-700' : 'border-stone-200 text-stone-600 hover:bg-stone-50'}`} data-id="element-1671" data-mp="lh0rjibs06c">
                      <span className="h-2 w-2 rounded-full" style={{
                  backgroundColor: employee.color
                }} data-id="element-1672" data-mp="2l5yptkxamzcb" />
                      {employee.name}
                    </button>;
            })}
              </div>
            </fieldset>
          </section>
        </div>}

      {tab === 'timer' && <div className="space-y-5" data-id="element-1673" data-mp="33w6onqtyn3mp">
          <section className={`${card} p-5`} data-id="element-1674" data-mp="33x819zqmjy6j">
            <h2 className="mb-4 text-base font-semibold text-stone-900" data-id="element-1675" data-mp="38ui025zwlm71">
              Registrer tid
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-4" data-id="element-1676" data-mp="2jqt4xqwdyu1g">
              <select value={timeForm.employeeId} onChange={e => setTimeForm({
            ...timeForm,
            employeeId: e.target.value
          })} aria-label="Medarbejder" className={field} data-id="element-1677" data-mp="2eshv3g63cjj8">
                {employees.map(employee => <option key={employee.id} value={employee.id} data-id="element-1678" data-mp="210bghyil6klw">
                    {employee.name}
                  </option>)}
              </select>
              <select value={timeForm.workType} onChange={e => setTimeForm({
            ...timeForm,
            workType: e.target.value as WorkType
          })} aria-label="Arbejdstype" className={field} data-id="element-1679" data-mp="29tenbnyq7z7k">
                {WORK_TYPES.map(type => <option key={type} value={type} data-id="element-1680" data-mp="137ljwhmr35xt">
                    {type}
                  </option>)}
              </select>
              <input value={timeForm.hours} onChange={e => setTimeForm({
            ...timeForm,
            hours: e.target.value
          })} placeholder="Timer, f.eks. 2,5" inputMode="decimal" aria-label="Timer" className={field} data-id="element-1681" data-mp="17zd9opy317mw" />
              <button onClick={addTime} className="rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1682" data-mp="1lw8jwlq2l54j">
                Tilføj
              </button>
            </div>
            <input value={timeForm.note} onChange={e => setTimeForm({
          ...timeForm,
          note: e.target.value
        })} placeholder="Note (valgfri)" className={`${field} mt-3`} data-id="element-1683" data-mp="1gxfb36lqa2ap" />
          </section>

          <section className={`${card} overflow-hidden`} data-id="element-1684" data-mp="31lso4mtjlpby">
            <header className="flex items-center justify-between gap-3 border-b border-stone-100 px-5 py-3" data-id="element-1685" data-mp="36jc1zlmbw03d">
              <h2 className="text-base font-semibold text-stone-900" data-id="element-1686" data-mp="2skcguny0y3by">
                Registreret på opgaven
              </h2>
              <span className="text-sm font-semibold tabular-nums text-stone-900" data-id="element-1687" data-mp="2xhkvwfnvlr8z">
                {registered.toLocaleString('da-DK')} t
              </span>
            </header>
            <TimeEntryList entries={own} emptyTitle="Ingen timer registreret" onUpdate={onUpdateTime} onRemove={onRemoveTime} data-id="element-1688" data-mp="2nkzq3s418c43" />
          </section>
        </div>}

      {tab === 'materialer' && <div className="space-y-5" data-id="element-1689" data-mp="1zv5jvte7xlst">
          <section className={`${card} p-5`} data-id="element-1690" data-mp="1zw6tdtfl608f">
            <h2 className="mb-1 text-base font-semibold text-stone-900" data-id="element-1691" data-mp="1zxhwrs54d5z0">
              Tilføj til opgaven
            </h2>
            <p className="mb-4 text-sm text-stone-500" data-id="element-1692" data-mp="1l86us52u33h9">
              Materialer fra kartoteket og ydelser som servicebil, kørsel og
              dokumentation.
            </p>
            <JobLineAdder materials={materials} products={products} onAdd={line => onUpdate({
          materials: [...job.materials, line]
        })} data-id="element-1693" data-mp="1gbq0su8yduw9" />
          </section>

          <section className={`${card} overflow-hidden`} data-id="element-1694" data-mp="2469atudelq2x">
            {job.materials.length === 0 ? <EmptyState icon={PackageIcon} title="Intet tilføjet endnu" description="Husk servicebil og kørsel — de tælles med på fakturaen." data-id="element-1695" data-mp="1z76cstvuck4g" /> : <>
                <ul className="divide-y divide-stone-100" data-id="element-1696" data-mp="290djqj68c86n">
                  {job.materials.map(item => <li key={item.id} className="flex items-center gap-4 px-5 py-3" data-id="element-1697" data-mp="2iw760u9r3g1m">
                      <span className="min-w-0 flex-1" data-id="element-1698" data-mp="2q0q6viyxedxp">
                        <span className="block truncate text-sm font-medium text-stone-900" data-id="element-1699" data-mp="2uy8d31lk6f20">
                          {item.name}
                        </span>
                        <span className="flex items-center gap-1.5 text-xs text-stone-400" data-id="element-1700" data-mp="2zw9u7gzfc4au">
                          {item.productId && <WrenchIcon className="h-3 w-3 text-brand-600" data-id="element-1701" data-mp="38qeibsn6b1mo" />}
                          <span className="font-mono" data-id="element-1702" data-mp="3dogb55aeifbc">{item.itemNo}</span>
                        </span>
                      </span>
                      <span className="text-sm tabular-nums text-stone-600" data-id="element-1703" data-mp="3iolxq71tdbmk">
                        {item.quantity.toLocaleString('da-DK')}
                        {item.unit ? ` ${item.unit}` : ''} ×{' '}
                        {kr(item.salesPrice)}
                      </span>
                      <span className="w-24 text-right text-sm font-semibold tabular-nums text-stone-900" data-id="element-1704" data-mp="1ta5zs24zv71u">
                        {kr(item.quantity * item.salesPrice)}
                      </span>
                      <button onClick={() => onUpdate({
                materials: job.materials.filter(m => m.id !== item.id)
              })} aria-label={`Fjern ${item.name}`} className="rounded-lg p-1.5 text-stone-300 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-1705" data-mp="5m8nuece5jc4">
                        <Trash2Icon className="h-4 w-4" data-id="element-1706" data-mp="kawf48f7f6w9" />
                      </button>
                    </li>)}
                </ul>
                <div className="flex items-center justify-between border-t border-stone-200 bg-stone-50 px-5 py-3" data-id="element-1707" data-mp="352ezfxesb6vd">
                  <span className="text-sm font-medium text-stone-600" data-id="element-1708" data-mp="302sojumfi2pq">
                    Materialer og ydelser i alt
                  </span>
                  <span className="text-sm font-bold tabular-nums text-stone-900" data-id="element-1709" data-mp="rtchfro4kxwb">
                    {kr(materialTotal)}
                  </span>
                </div>
              </>}
          </section>
        </div>}

      {tab === 'billeder' && <section className={`${card} p-5`} data-id="element-1710" data-mp="nu3nomt3500r">
          <button onClick={() => onUpdate({
        photos: [...job.photos, {
          id: newLineId(),
          url: '',
          caption: 'Nyt billede',
          uploadedAt: new Date().toISOString()
        }]
      })} className="flex w-full flex-col items-center gap-2 rounded-xl border border-dashed border-stone-300 px-6 py-10 text-sm font-medium text-stone-500 transition-colors hover:border-brand-300 hover:text-brand-700" data-id="element-1711" data-mp="nqo7thrl7mya">
            <CameraIcon className="h-6 w-6" data-id="element-1712" data-mp="3jpzufw4skjlv" />
            Tilføj billede fra kameraet
          </button>

          {job.photos.length > 0 && <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-1713" data-mp="3eqdkbvk9fjha">
              {job.photos.map(photo => <li key={photo.id} className="overflow-hidden rounded-lg border border-stone-200" data-id="element-1714" data-mp="3sq4zw0qfd7xe">
                  <div className="flex aspect-square items-center justify-center bg-stone-100" data-id="element-1715" data-mp="301hd20q12ahq">
                    <ImageIcon className="h-6 w-6 text-stone-300" data-id="element-1716" data-mp="2w1gmjhpnm8ub" />
                  </div>
                  <p className="truncate px-2 py-1.5 text-xs text-stone-600" data-id="element-1717" data-mp="39slrmzbsjtc1">
                    {photo.caption}
                  </p>
                </li>)}
            </ul>}
        </section>}

      {tab === 'dokumenter' && <EmptyState icon={FileIcon} title="Ingen dokumenter" description="Tegninger og aftaler til denne opgave lægges her." data-id="element-1718" data-mp="2h9ojo571qn9h" />}

      {tab === 'noter' && <section className={`${card} p-5`} data-id="element-1719" data-mp="g7fju14u6da6">
          <label className="block" data-id="element-1720" data-mp="l7uwsrl7hirm">
            <span className="mb-2 block text-sm font-medium text-stone-700" data-id="element-1721" data-mp="l6s6akrspic8">
              Noter til opgaven
            </span>
            <textarea value={job.notes} onChange={e => onUpdate({
          notes: e.target.value
        })} rows={8} placeholder="Adgangsforhold, aftaler med kunden, hvad der mangler…" className={`${field} leading-relaxed`} data-id="element-1722" data-mp="q1pfbrp2rrp4" />
          </label>
        </section>}

      {/* Desktop actions */}
      <div className="mt-6 hidden gap-3 lg:flex" data-id="element-1723" data-mp="77aerxn47qz6">{actions}</div>

      {/* Phone action bar — thumb-sized, always reachable */}
      <div className="fixed inset-x-0 bottom-[52px] z-30 flex gap-2 border-t border-stone-200 bg-white/95 px-3 py-2.5 backdrop-blur lg:hidden" data-id="element-1724" data-mp="b842apwnb3rj">
        {actions}
      </div>
    </div>;
}